/**
 * AppDropdown — Non-searchable select dropdown
 *
 * Features:
 *  - Shows up to 6 items at once; custom styled scrollbar appears when list > 6
 *  - No native scrollbar (hidden via CSS)
 *  - Lazy / infinite scroll: pass fetchOptions(search, page, pageSize) for async mode
 *  - Fully backward-compatible: existing `options=[]` prop continues to work unchanged
 *
 * Props:
 *  value           - current selected value (string | number)
 *  onChange        - (value) => void
 *  options         - static option list [{ value, label, icon?, disabled? }]
 *  fetchOptions    - async (search, page, pageSize) => { items: [{value,label}], hasMore }
 *                   (use EITHER options OR fetchOptions, not both)
 *  initialLabel    - label to display when value is set but item not yet in fetched list (edit mode)
 *  placeholder     - trigger placeholder text
 *  disabled        - disable the trigger
 *  className       - wrapper div class
 *  triggerClassName- trigger button class
 *  menuClassName   - menu panel class
 *  align           - 'left' | 'right'
 *  pageSize        - items per fetch page (async mode, default 20)
 */

import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useInfiniteOptions } from '../hooks/useInfiniteOptions'

const ITEM_HEIGHT = 40   // px — must match item padding in CSS
const VISIBLE_ITEMS = 6  // items visible before scrollbar appears
const LIST_MAX_HEIGHT = ITEM_HEIGHT * VISIBLE_ITEMS  // 240px

export default function AppDropdown({
  value,
  onChange,
  options: staticOptions = [],
  fetchOptions,
  initialLabel,
  placeholder = 'Select',
  disabled = false,
  className = '',
  triggerClassName = '',
  menuClassName = '',
  align = 'left',
  pageSize = 20,
}) {
  const [open, setOpen] = useState(false)
  const listRef = useRef(null)
  const trackRef = useRef(null)
  const thumbRef = useRef(null)

  // ── Data (static or async) ────────────────────────────────────────────────
  const { items, isLoading, sentinelRef } = useInfiniteOptions({
    fetchOptions,
    staticOptions: fetchOptions ? undefined : staticOptions,
    pageSize,
    enabled: open,
    // Pre-seed with the current value so the label shows immediately in edit mode
    // (before the dropdown is opened and the first page of options is fetched).
    seedOption: value && initialLabel ? { value: String(value), label: initialLabel } : null,
  })

  // ── Selected label ────────────────────────────────────────────────────────
  const selected = useMemo(
    () => items.find((item) => String(item.value) === String(value)) ?? null,
    [items, value],
  )

  // When value is set but the item hasn't been fetched yet (edit mode), show initialLabel.
  // Uses selected?.label first (found in fetched items), then falls back to initialLabel.
  const displayLabel = selected?.label ?? (initialLabel || null)

  const needsScroll = items.length > VISIBLE_ITEMS

  // ── Custom scrollbar sync ─────────────────────────────────────────────────
  useEffect(() => {
    const list = listRef.current
    const thumb = thumbRef.current
    const track = trackRef.current
    if (!list || !thumb || !track) return
    if (!needsScroll) return

    function syncThumb() {
      const { scrollTop, scrollHeight, clientHeight } = list
      const trackH = track.clientHeight
      const thumbH = Math.max(28, (clientHeight / scrollHeight) * trackH)
      const maxTop = trackH - thumbH
      const top = (scrollTop / (scrollHeight - clientHeight)) * maxTop
      thumb.style.height = `${thumbH}px`
      thumb.style.transform = `translateY(${top}px)`
    }

    syncThumb()
    list.addEventListener('scroll', syncThumb, { passive: true })
    return () => list.removeEventListener('scroll', syncThumb)
  }, [needsScroll, items.length, open])

  const panelAlign = align === 'right' ? 'end' : 'start'
  const selectedValue = String(selected?.value ?? '')

  return (
    <DropdownMenu.Root modal={false} open={open} onOpenChange={setOpen}>
      <div className={`relative ${className}`}>
        <DropdownMenu.Trigger asChild>
          <button
            type="button"
            disabled={disabled}
            className={`flex w-full min-w-0 items-center justify-between gap-2 rounded-xl border border-outline-variant/30 bg-surface-container-lowest px-3 py-2 text-left text-xs font-semibold text-on-surface shadow-xs transition hover:border-primary/35 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/25 disabled:cursor-not-allowed disabled:opacity-60 ${triggerClassName}`}
          >
            <span className={`min-w-0 flex-1 truncate text-left ${displayLabel ? 'text-on-surface' : 'text-on-surface-variant'}`}>
              {displayLabel ?? placeholder}
            </span>
            <span
              className="material-symbols-outlined text-base text-on-surface-variant shrink-0 transition-transform duration-200"
              style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}
            >
              expand_more
            </span>
          </button>
        </DropdownMenu.Trigger>

        <DropdownMenu.Portal>
          <DropdownMenu.Content
            align={panelAlign}
            sideOffset={8}
            collisionPadding={12}
            className={`z-[220] min-w-[var(--radix-dropdown-menu-trigger-width)] overflow-hidden rounded-2xl border border-outline-variant/20 bg-surface-container-lowest py-1.5 shadow-[0_14px_32px_rgba(15,23,42,0.2)] backdrop-blur-xl ${menuClassName}`}
          >
            <DropdownMenu.RadioGroup
              value={selectedValue}
              onValueChange={(nextValue) => {
                const match = items.find((item) => String(item.value) === String(nextValue))
                if (!match || match.disabled) return
                onChange?.(match.value)
                setOpen(false)
              }}
            >
              {/* Scrollable list with custom scrollbar */}
              <div className="relative">
                <div
                  ref={listRef}
                  className="app-dropdown-list"
                  style={{
                    maxHeight: `${LIST_MAX_HEIGHT}px`,
                    overflowY: needsScroll ? 'scroll' : 'visible',
                    scrollbarWidth: 'none',        /* Firefox */
                    msOverflowStyle: 'none',       /* IE */
                  }}
                >
                  {/* Webkit scrollbar hidden via global style: .app-dropdown-list::-webkit-scrollbar { display:none } */}
                  {items.map((item, idx) => {
                    const itemValue = String(item.value)
                    const isSelected = itemValue === selectedValue
                    return (
                      <DropdownMenu.RadioItem
                        key={`${itemValue}-${idx}`}
                        value={itemValue}
                        disabled={Boolean(item.disabled)}
                        style={{ minHeight: `${ITEM_HEIGHT}px` }}
                        className={`group flex w-full cursor-pointer items-center px-3.5 py-2 text-left text-on-surface outline-none transition data-[disabled]:cursor-not-allowed data-[disabled]:opacity-45 ${item.icon ? 'gap-2.5' : 'gap-0'} ${isSelected ? 'font-semibold text-primary' : 'font-normal'} focus:bg-surface-container-low hover:bg-surface-container-low`}
                      >
                        {item.icon ? (
                          <span className="material-symbols-outlined text-base text-on-surface-variant">
                            {item.icon}
                          </span>
                        ) : null}
                        <span className="text-sm leading-tight">{item.label}</span>
                        <DropdownMenu.ItemIndicator className="ml-auto">
                          <span className="material-symbols-outlined text-sm text-primary">check</span>
                        </DropdownMenu.ItemIndicator>
                      </DropdownMenu.RadioItem>
                    )
                  })}

                  {/* Infinite scroll sentinel */}
                  <div ref={sentinelRef} style={{ height: 1 }} />

                  {isLoading ? (
                    <div className="flex items-center justify-center py-2.5">
                      <span className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                    </div>
                  ) : null}

                  {items.length === 0 && !isLoading ? (
                    <div className="px-4 py-3 text-sm text-on-surface-variant">No options available.</div>
                  ) : null}
                </div>

                {/* Custom scrollbar track + thumb */}
                {needsScroll ? (
                  <div
                    ref={trackRef}
                    className="pointer-events-none absolute right-[3px] bottom-1 top-1 w-[3px] rounded-full bg-outline-variant/20"
                  >
                    <div
                      ref={thumbRef}
                      className="absolute right-0 top-0 w-full rounded-full bg-primary/40 transition-opacity"
                    />
                  </div>
                ) : null}
              </div>
            </DropdownMenu.RadioGroup>
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </div>
    </DropdownMenu.Root>
  )
}
